import { useEffect, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  addSectionGridsAtom,
  collectionChosenByUserAtom,
  crossSectionArmedAtom,
  sectionGridIdsAtom,
  selectCollectionAtom,
  structureGridCollectionIdAtom,
} from './atoms';
import {
  useStructureGridCollection,
  useStructureGridCollections,
  type CollectionGrid,
  type CollectionSummary,
} from './use-structure-grid-collections';
import { useIntersectingStructureGrids } from './use-intersecting-structure-grids';
import { useSectionLineVertices } from './hooks';

/**
 * The same basin under two spellings is one basin: these are free-text columns on two
 * different tables, and test data carries stray casing and whitespace.
 *
 * @param {string | null} name - A basin as either table writes it.
 * @return {string} The comparable form.
 */
const basinKey = (name: string | null): string =>
  (name ?? '').trim().toLowerCase();

/**
 * The newest of some collections, by when it was made.
 *
 * Product's tie-break: where more than one collection could serve, the most recently
 * created is the one someone has been curating, so it is the one to open on.
 *
 * @param {Array} candidates - The collections to choose between.
 * @return {CollectionSummary | null} The newest, or null where there are none.
 */
const newest = (candidates: CollectionSummary[]): CollectionSummary | null =>
  candidates.reduce<CollectionSummary | null>((best, candidate) => {
    if (best === null || candidate.createdAt > best.createdAt) {
      return candidate;
    }
    return best;
  }, null);

/**
 * Which basins the section runs through, most-crossed first.
 *
 * Taken from the grids the line actually crosses rather than from the map's viewport:
 * the grids are what the section will sample, so they are what "where is this section"
 * means here.
 *
 * @param {Array} crossed - The grids the line crosses.
 * @return {Array} The basin keys, most-crossed first.
 */
const basinsCrossed = (crossed: CollectionGrid[]): string[] => {
  const counts = new Map<string, number>();
  crossed.forEach((grid) => {
    const key = basinKey(grid.basinName);
    if (key !== '') {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([basin]) => basin);
};

/**
 * Gives an armed section something to plot without the user going looking: a collection
 * and its whole stack. Run from the drawer rather than the settings panel — a section
 * blank until you find the gear looks broken — sharing one cache entry.
 *
 * Which collection, per product: the Default one, then the most recently created.
 *
 * The subtlety is that "the Default" is not one collection. `is_default` is held per
 * basin — the model enforces one default per basin — so a list read straight through
 * offers eight or ten of them, and taking the first is taking whichever basin happens
 * to sort first. On production that is Williston, which is why a Midland section opened
 * on a Williston stack and UAT read it as a stale selection.
 *
 * So the default is chosen by basin, and the basin comes from the grids the line
 * crosses. Until a line exists there is nothing to match on, and the newest default is
 * the opening guess. Where the line's basin has no default at all, the newest collection
 * the line crosses serves instead.
 *
 * A default its line does not cross is left alone: product would rather keep the
 * collection they expect — the picker badges it "Not crossed" — than be moved somewhere
 * they did not choose. And a collection the user picked is an instruction: it stands,
 * crossed or not.
 *
 * A host that already knows the collection passes it, and it is opened on ahead of all
 * of the above — still giving way to the user's own pick.
 *
 * @param {number | null} defaultCollectionId - The host's collection, or null.
 */
export const useCrossSectionBootstrap = (
  defaultCollectionId: number | null = null
): void => {
  const armed = useAtomValue(crossSectionArmedAtom);
  const collectionId = useAtomValue(structureGridCollectionIdAtom);
  const gridIds = useAtomValue(sectionGridIdsAtom);
  const selectCollection = useSetAtom(selectCollectionAtom);
  const addGrids = useSetAtom(addSectionGridsAtom);

  const { collections } = useStructureGridCollections(armed);
  const { grids } = useStructureGridCollection(collectionId);

  const chosenByUser = useAtomValue(collectionChosenByUserAtom);
  const line = useSectionLineVertices();
  const {
    grids: crossedGrids,
    intersectingCollectionIds,
    answered,
  } = useIntersectingStructureGrids(line, collectionId, armed);

  // What the section should be on, as far as anything known so far can say. Null while
  // there is nothing to go on, which leaves whatever is selected where it is.
  const preferred = useMemo<number | null>(() => {
    if (!armed || collections.length === 0) {
      return null;
    }
    if (
      defaultCollectionId !== null &&
      collections.some((collection) => collection.id === defaultCollectionId)
    ) {
      return defaultCollectionId;
    }
    const defaults = collections.filter((collection) => collection.isDefault);

    // Before the line has been asked about, its basin is unknown and the newest default
    // is the whole of what there is to go on — but only for a section holding no
    // collection at all.
    //
    // Once one is held, an unanswered question has to leave it alone. Selecting a
    // collection re-keys the grids query — its id rides in the key, since the average
    // thickness it returns is a property of the grid's place in that collection — so
    // `answered` goes false again for as long as the refetch is in flight. Answering
    // "the newest default" in that window pulls the section off the basin's default and
    // back to whichever basin's default is newest, which re-keys the query, which
    // reopens the window. The pick and the question then chase each other for as long
    // as the refetches keep missing cache, which on a slow environment is forever.
    if (!answered) {
      return collectionId === null ? (newest(defaults)?.id ?? null) : null;
    }

    // The default for the basin the section runs through — the answer product asked for.
    const basins = basinsCrossed(crossedGrids);
    const forBasin = basins
      .map((basin) =>
        defaults.find((collection) => basinKey(collection.basin) === basin)
      )
      .find((collection) => collection !== undefined);
    if (forBasin) {
      return forBasin.id;
    }

    // That basin keeps no default, so the newest collection the line crosses stands in.
    const crossed = collections.filter((collection) =>
      intersectingCollectionIds.has(collection.id)
    );
    return newest(crossed)?.id ?? newest(defaults)?.id ?? null;
  }, [
    armed,
    collections,
    defaultCollectionId,
    answered,
    collectionId,
    crossedGrids,
    intersectingCollectionIds,
  ]);

  // Held to that preference for as long as the choice is the section's own. The first
  // pick and every later correction are the same act, so they are one effect.
  useEffect(() => {
    if (
      !armed ||
      chosenByUser ||
      preferred === null ||
      preferred === collectionId
    ) {
      return;
    }
    selectCollection({
      collectionId: preferred,
      gridIds: [],
      automatic: true,
    });
  }, [armed, chosenByUser, preferred, collectionId, selectCollection]);

  // The collection's whole stack goes on the section the first time its grids are
  // read: the grids are what the collection is for, and a chosen collection with an
  // empty plot reads as a failure.
  useEffect(() => {
    if (collectionId !== null && gridIds.length === 0 && grids.length > 0) {
      addGrids(grids.map((grid) => grid.structureGridId));
    }
    // Only on arrival of a collection's grids: re-running when the user removes grids
    // would put them straight back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, grids]);
};
